export interface IconProps {
  uiId: string;
  /** 見 ICON_PATH 的 key；未知名稱會退回問號圖示，不會整個畫面炸掉。 */
  name: string;
  /** 有值＝這個圖示自己帶意義，會以 role="img" + aria-label 曝露；沒有值＝純裝飾，對輔具隱藏。 */
  label?: string;
}

/**
 * 圖示對照表。key 是語意名稱，value 是 24x24 viewBox 的 SVG path（stroke 畫法）。
 *
 * 原系統用 Font Awesome（WebContent/css/font-awesome.min.css，靠 @font-face 載入字型檔），
 * §3.2 禁止外部 CDN 與字型檔，所以雛形改成內嵌 SVG。形狀是「同語意的通用圖示」，
 * 不是 Font Awesome 的實際字形 —— 外觀一定有差，【雛形值待實機校準】。
 *
 * ponytail: 只放目前真的用得到的少數幾個，不做完整 icon set、不做動態 import。
 * 需要新圖示時就在這張表加一行；表變得很大的時候才值得考慮換成 sprite。
 */
const ICON_PATH: Record<string, string> = {
  search: 'M10 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14M15 15l6 6',
  plus: 'M12 5v14M5 12h14',
  edit: 'M4 20h4L19 9l-4-4L4 16v4M14 6l4 4',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
  close: 'M5 5l14 14M19 5L5 19',
  check: 'M5 13l4 4L19 7',
  'chevron-down': 'M6 9l6 6 6-6',
  'chevron-right': 'M9 6l6 6-6 6',
  download: 'M12 4v11M7 12l5 5 5-5M5 20h14',
  warning: 'M12 4 2 20h20L12 4M12 10v5M12 18h.01',
  // 側邊選單收合鈕（layout/navbar.jsp 的 fa-bars）與選單常用圖示
  bars: 'M4 6h16M4 12h16M4 18h16',
  home: 'M4 11l8-7 8 7M6 10v10h12V10',
  'file-o': 'M6 3h8l4 4v14H6zM14 3v4h4',
  print: 'M7 8V3h10v5M7 18H5v-6h14v6h-2M7 14h10v7H7z',
  'sign-out': 'M10 4H5v16h5M14 8l4 4-4 4M18 12H9',
};

/** 未知名稱的 fallback：圓框問號。維持版面不塌，同時看得出「這裡有東西沒對上」。 */
const FALLBACK_PATH =
  'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M9.5 9.5a2.5 2.5 0 1 1 3 2.4V14M12 17h.01';

/**
 * 圖示。尺寸用 1em，跟著周圍文字大小走。
 */
export function Icon({ uiId, name, label }: IconProps) {
  const path = ICON_PATH[name] ?? FALLBACK_PATH;
  const decorative = label === undefined;

  return (
    <svg
      data-ui-id={uiId}
      data-icon-name={name}
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="inline-block align-[-0.125em]"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={label}
    >
      {decorative ? null : <title>{label}</title>}
      <path d={path} />
    </svg>
  );
}
